<script setup lang="ts">
import type { DateValue } from '@internationalized/date'
import { getLocalTimeZone, today, CalendarDate } from '@internationalized/date'
import { z } from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { Calendar } from '~/components/ui/calendar'
import { Checkbox } from '~/components/ui/checkbox'
import { Label } from '~/components/ui/label'
import { Switch } from '~/components/ui/switch'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select'
import { Input } from '~/components/ui/input'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { LucideAlertCircle, LucideCalendar, AlertCircle } from 'lucide-vue-next'
import {
  useRateAlerts,
  type IRateAlertCreate,
  type IRateAlertUpdate,
  type RateAlertCondition
} from '~/composables/useRateAlerts'
import { useCurrencyStore } from '~/stores/currencies'
import type {
  IFeatureRateAlertFormProps,
  IFeatureRateAlertFormEmits,
  IOrganizationRates
} from './types'

const props = withDefaults(defineProps<IFeatureRateAlertFormProps>(), {
  defaultType: 'buy'
})
const emit = defineEmits<IFeatureRateAlertFormEmits>()

const { t } = useI18n()
const { trackEvent } = useAnalytics()
const {
  createRateAlert,
  updateRateAlert,
  validateRateTarget: validateRateTargetFn
} = useRateAlerts()
const currencyStore = useCurrencyStore()

const allBanks = ref(false)
const selectedBankId = ref<string | undefined>(undefined)
const serverError = ref<string | null>(null)
const bankError = ref<string | null>(null)
const banks = ref<Array<{ id: string; title: string }>>([])
const isLoading = ref(false)
const showSuccess = ref(false)
const isOpenDate = ref(false)
const selectedDate = ref<DateValue | undefined>(
  props.alert ? undefined : today(getLocalTimeZone()).add({ days: 7 })
)
const cashless = ref(false)
const condition = ref<RateAlertCondition>('lte')

const banksList = computed(() => {
  if (props.allOrganizationsRates?.length) {
    return props.allOrganizationsRates.map((b) => ({ id: String(b.id), title: b.title }))
  }
  return banks.value
})

const maxDate = computed(() => {
  const currentDate = today(getLocalTimeZone())
  return currentDate.add({ days: 7 })
})

// Track current type separately to avoid circular dependency with useForm
const currentType = ref<'buy' | 'sell'>(props.defaultType)

// Selected currency with USD fallback
const selectedCurrency = ref<string>(props.currencyCode || 'USD')

// Fetch currencies if not already loaded
if (!currencyStore.loaded) {
  currencyStore.fetchCurrencies()
}

// Filter currencies for rates page (USD, EUR, RUB, KZT, CNY - always exchanged with KGS)
const availableCurrencies = computed(() => {
  const rateCurrencies = ['USD', 'EUR', 'RUB', 'KZT', 'CNY']
  return currencyStore.list.filter((c) => rateCurrencies.includes(c.code))
})

const currencyOptions = computed(() => {
  return availableCurrencies.value.map((c) => ({
    id: c.id,
    label: c.code,
    value: c.code
  }))
})

// Compute average rates from allOrganizationsRates
const averageRates = computed(() => {
  if (!props.allOrganizationsRates?.length) return undefined
  const result: Partial<IOrganizationRates> = {}
  const currencies = ['usd', 'eur', 'rub', 'kzt', 'cny']
  const types = ['buy', 'sell']
  for (const currency of currencies) {
    for (const type of types) {
      const key = `${type}_${currency}` as keyof IOrganizationRates
      const vals = props.allOrganizationsRates
        .map((b) => b.rates?.[key])
        .filter((v) => v !== undefined && v !== null)
        .map((v) => Number(v))
      if (vals.length > 0) {
        result[key] = String(vals.reduce((a, b) => a + b, 0) / vals.length)
      }
    }
  }
  return result
})

// Current rate for validation - uses allOrganizationsRates
const currentRateForValidation = computed(() => {
  const code = selectedCurrency.value?.toLowerCase()
  const type = currentType.value
  if (!code || !type) return undefined

  const rateKey = `${type}_${code}` as keyof IOrganizationRates

  if (allBanks.value) {
    return averageRates.value?.[rateKey] ? Number(averageRates.value[rateKey]) : undefined
  }

  const bankId = selectedBankId.value || props.defaultBankId
  if (bankId) {
    const bank = props.allOrganizationsRates?.find((b) => String(b.id) === String(bankId))
    return bank?.rates?.[rateKey] ? Number(bank.rates[rateKey]) : undefined
  }

  return undefined
})

// Selected amount initialized from current rate
const selectedAmount = ref<number | undefined>(undefined)

defineExpose({
  reset: () => {
    resetFormState()
    resetForm()
    setDefaultFormValues()
  }
})

// Fetch banks from API
const { data: banksData } = await useFetchLocale<{
  data: Array<{ id: number; title: string; logo: string; url: string }>
}>('/api/rates/organizations-cached')

watch(
  banksData,
  (fetchedBanks) => {
    if (fetchedBanks?.data && fetchedBanks.data.length > 0) {
      banks.value = fetchedBanks.data.map((bank) => ({
        id: String(bank.id),
        title: bank.title
      }))
    }
  },
  { immediate: true }
)

// Computed to determine mode
const isEditMode = computed(() => !!props.alert)
const alertId = computed(() => props.alert?.id)

// Formatted validation range for error message
const validationMinFormatted = computed(() => {
  const rate = currentRateForValidation.value
  if (!rate) return '0'
  const currencyId = useGetCurrencyIdByCode(selectedCurrency.value.toLowerCase())
  return useCurrencyFormat(rate * 0.5, currencyId)
})

const validationMaxFormatted = computed(() => {
  const rate = currentRateForValidation.value
  if (!rate) return '0'
  const currencyId = useGetCurrencyIdByCode(selectedCurrency.value.toLowerCase())
  return useCurrencyFormat(rate * 1.5, currencyId)
})

const rateAlertSchema = computed(() => {
  const maxDateStr = maxDate.value.toString()
  return toTypedSchema(
    z.object({
      type: z.enum(['buy', 'sell']),
      value: z
        .number({
          required_error: t('rate_alert.value_required'),
          invalid_type_error: t('rate_alert.value_required')
        })
        .min(0.01, { message: t('rate_alert.value_min') })
        .refine(
          (val) => {
            const rateToValidate = currentRateForValidation.value
            if (rateToValidate && validateRateTargetFn) {
              return validateRateTargetFn(rateToValidate, val)
            }
            return true
          },
          {
            message: t('rate_alert.value_out_of_range', {
              min: validationMinFormatted.value,
              max: validationMaxFormatted.value
            })
          }
        ),
      expired_at: z
        .string()
        .nullable()
        .optional()
        .refine(
          (val) => {
            if (!val) return true
            return val <= maxDateStr
          },
          {
            message: t('rate_alert.date_max_7_days')
          }
        )
    })
  )
})

const { handleSubmit, setFieldValue, resetForm, values } = useForm({
  validationSchema: rateAlertSchema,
  initialValues: {
    type: props.defaultType,
    expired_at: props.alert ? null : today(getLocalTimeZone()).add({ days: 7 }).toString()
  }
})

// Round rate value to max 3 decimal places
const roundRateValue = (rate: number): number => {
  const decimals = String(rate).split('.')[1]?.length || 0
  return decimals > 3 ? Number(rate.toFixed(3)) : rate
}

// Set default form values after resetForm() - must be called after vee-validate reset
const setDefaultFormValues = () => {
  const rate = currentRateForValidation.value
  if (rate !== undefined) {
    const roundedRate = roundRateValue(rate)
    selectedAmount.value = roundedRate
    setFieldValue('value', roundedRate)
  }
  setFieldValue('type', props.defaultType)
}

// Auto-populate amount from current rate when dependencies change (skip in edit mode)
watch(
  [selectedCurrency, currentType, selectedBankId, allBanks],
  () => {
    if (isEditMode.value) return
    const rate = currentRateForValidation.value
    if (rate !== undefined) {
      const roundedRate = roundRateValue(rate)
      selectedAmount.value = roundedRate
      setFieldValue('value', roundedRate)
    }
  },
  { immediate: true }
)

// Initialize bank selection on mount
watch(
  [() => props.alert, banksList],
  ([alert, list]) => {
    // Edit mode - use alert data
    if (alert) {
      selectedBankId.value = alert.bank_id ? String(alert.bank_id) : undefined
      allBanks.value = !alert.bank_id
      return
    }

    // Create mode - initialize bank selection
    if (props.defaultBankId) {
      selectedBankId.value = String(props.defaultBankId)
      allBanks.value = false
    } else if (list.length > 0) {
      selectedBankId.value = list[0].id
      allBanks.value = false
    } else {
      selectedBankId.value = undefined
      allBanks.value = true
    }
  },
  { immediate: true }
)

// Populate form when alert prop changes (edit mode)
watch(
  () => props.alert,
  (alert) => {
    if (alert) {
      selectedCurrency.value = alert.currency_code
      selectedAmount.value = alert.value
      currentType.value = alert.type
      setFieldValue('type', alert.type)
      setFieldValue('value', alert.value)

      // Convert expired_at string to DateValue if exists
      if (alert.expired_at) {
        const date = new Date(alert.expired_at)
        selectedDate.value = new CalendarDate(
          date.getFullYear(),
          date.getMonth() + 1,
          date.getDate()
        )
        setFieldValue('expired_at', alert.expired_at)
      } else {
        selectedDate.value = undefined
        setFieldValue('expired_at', null)
      }

      // Set rate_type
      if (alert.rate_type) {
        cashless.value = alert.rate_type === 'cashless'
      }

      // Set condition
      condition.value = alert.condition ?? 'lte'
    }
  },
  { immediate: true }
)

// Sync currentType with form value
watch(
  () => values.type,
  (newType) => {
    if (newType) {
      currentType.value = newType
    }
  }
)

watch(allBanks, (isAllBanks) => {
  bankError.value = null
  if (!isAllBanks && !selectedBankId.value && props.defaultBankId) {
    // Switching to specific bank mode without selection - use default
    selectedBankId.value = String(props.defaultBankId)
  }
})

// Clear bank error when bank is selected
watch(selectedBankId, (newBankId) => {
  bankError.value = null
  // If a bank is selected from dropdown, uncheck "all banks"
  if (newBankId && allBanks.value) {
    allBanks.value = false
  }
})

watch([selectedAmount, selectedCurrency], ([amount]) => {
  setFieldValue('value', amount)
})

watch(selectedDate, (newDate) => {
  if (newDate) {
    setFieldValue('expired_at', newDate.toString())
  } else {
    setFieldValue('expired_at', null)
  }
})

const resetFormState = () => {
  selectedCurrency.value = props.currencyCode || 'USD'
  selectedDate.value = today(getLocalTimeZone()).add({ days: 7 })
  currentType.value = props.defaultType
  cashless.value = false
  condition.value = 'lte'
  serverError.value = null
  bankError.value = null
  showSuccess.value = false

  // Reset bank selection
  if (props.defaultBankId) {
    selectedBankId.value = String(props.defaultBankId)
    allBanks.value = false
  } else if (banksList.value.length > 0) {
    selectedBankId.value = banksList.value[0].id
    allBanks.value = false
  } else {
    selectedBankId.value = undefined
    allBanks.value = true
  }
}

// Get bank_id for submit based on conditions
const getBankIdForSubmit = (): number | undefined => {
  if (allBanks.value) {
    return undefined
  }

  return selectedBankId.value ? Number(selectedBankId.value) : undefined
}

// Validate bank selection
const validateBankSelection = (): boolean => {
  if (allBanks.value) {
    bankError.value = null
    return true
  }

  if (!selectedBankId.value) {
    bankError.value = t('rate_alert.bank_required')
    return false
  }

  bankError.value = null
  return true
}

const formatToIsoDateTime = (dateStr: string | null | undefined): string | undefined => {
  if (!dateStr) return undefined
  if (dateStr.includes('T')) return dateStr
  // Parse as local midnight to avoid UTC date shift
  const [year, month, day] = dateStr.split('-').map(Number)
  const date = new Date(year, month - 1, day, 23, 59, 59)
  return date.toISOString()
}

const onSubmit = handleSubmit(async (formValues) => {
  const currencyCode = selectedCurrency.value
  if (!currencyCode) {
    serverError.value = t('rate_alert.currency_required')
    return
  }

  if (!selectedAmount.value) {
    serverError.value = t('rate_alert.value_required')
    return
  }

  if (!validateBankSelection()) {
    return
  }

  try {
    serverError.value = null
    isLoading.value = true
    emit('update:loading', true)

    // Determine bank_id based on conditions
    const bankId = getBankIdForSubmit()

    if (isEditMode.value && alertId.value) {
      // Update existing alert
      const updateData: IRateAlertUpdate = {
        type: formValues.type,
        currency_code: currencyCode,
        value: selectedAmount.value,
        condition: condition.value,
        rate_type: cashless.value ? 'cashless' : 'regular'
      }

      if (bankId) {
        updateData.bank_id = bankId
      }

      if (formValues.expired_at) {
        updateData.expired_at = formatToIsoDateTime(formValues.expired_at)
      }

      await updateRateAlert(alertId.value, updateData)
    } else {
      // Create new alert
      const submitData: IRateAlertCreate = {
        currency_code: currencyCode,
        type: formValues.type,
        value: selectedAmount.value,
        condition: condition.value,
        rate_type: cashless.value ? 'cashless' : 'regular'
      }

      if (bankId) {
        submitData.bank_id = bankId
      }

      if (formValues.expired_at) {
        submitData.expired_at = formatToIsoDateTime(formValues.expired_at)
      }

      await createRateAlert(submitData)
      trackEvent('rate_alert_subscribe', { currency: currencyCode })
    }

    showSuccess.value = true
    resetFormState()
    resetForm()
    emit('success')

    setTimeout(() => {
      showSuccess.value = false
    }, 3000)
  } catch (err: unknown) {
    serverError.value = (err as Error).message || t('rate_alert.create_error')
  } finally {
    isLoading.value = false
    emit('update:loading', false)
  }
})


const formatDate = (dateValue: DateValue) => {
  return dateValue.toDate(getLocalTimeZone()).toLocaleDateString('ru-RU')
}
</script>

<template>
  <div class="flex flex-col">
    <!-- Success Alert -->
    <Alert v-if="showSuccess" variant="green">
      <AlertCircle class="h-4 w-4" />
      <AlertTitle>{{ t('rate_alert.success_title') }}</AlertTitle>
      <AlertDescription>
        {{ isEditMode ? t('rate_alert.update_success') : t('rate_alert.create_success') }}
      </AlertDescription>
    </Alert>

    <!-- Form Content -->
    <template v-else>
      <div class="space-y-5 p-4">
      <!-- Bank Selection -->
      <div class="space-y-3">
        <template v-if="props.allOrganizationsRates && props.allOrganizationsRates.length > 0">
          <Label class="text-sm">{{ t('rate_alert.select_bank') }}</Label>
          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <!-- Specific Bank Card -->
            <div
              role="button"
              tabindex="0"
              :class="[
                'flex items-start gap-3 rounded-lg border-2 p-4 transition-all',
                !allBanks
                  ? 'border-blue-600 bg-blue-50 shadow-sm'
                  : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300'
              ]"
              @click="
                () => {
                  if (allBanks) allBanks = false
                }
              "
              @keydown.enter.space.prevent="() => { if (allBanks) allBanks = false }"
            >
              <div v-if="!allBanks" class="RadioChecked">
                <svg
                  class="h-3 w-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div v-else class="RadioUnchecked" />
              <div class="flex-1 space-y-1" @click.stop>
                <p class="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {{ t('rate_alert.selected_bank') }}
                </p>
                <Select v-model="selectedBankId" @update:model-value="allBanks = false">
                  <SelectTrigger
                    :aria-label="t('rate_alert.select_bank_placeholder')"
                    class="SelectTriggerTransparent"
                  >
                    <SelectValue
                      :placeholder="t('rate_alert.select_bank_placeholder')"
                      :class="[
                        'text-sm font-semibold',
                        !allBanks ? 'text-blue-900' : 'text-gray-700'
                      ]"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="org in props.allOrganizationsRates"
                      :key="org.id"
                      :value="String(org.id)"
                    >
                      {{ org.title }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <!-- All Banks Card -->
            <button
              type="button"
              :class="[
                'flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-all',
                allBanks
                  ? 'border-blue-600 bg-blue-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              ]"
              @click="allBanks = true"
            >
              <div v-if="allBanks" class="RadioChecked">
                <svg
                  class="h-3 w-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div v-else class="RadioUnchecked" />
              <div class="flex-1 space-y-1">
                <p class="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {{ t('rate_alert.monitoring') }}
                </p>
                <p :class="['text-sm font-semibold', allBanks ? 'text-blue-900' : 'text-gray-700']">
                  {{ t('rate_alert.all_banks') }}
                </p>
              </div>
            </button>
          </div>
        </template>
        <template v-else>
          <Label :class="['text-sm', bankError ? 'text-red-500' : '']">
            {{ t('rate_alert.select_bank') }}
          </Label>
          <Select v-model="selectedBankId" :disabled="allBanks">
            <SelectTrigger
              :aria-label="t('rate_alert.select_bank_placeholder')"
              :class="{ 'border-red-200 bg-red-50': bankError }"
            >
              <SelectValue
                :placeholder="
                  allBanks ? t('rate_alert.all_banks') : t('rate_alert.select_bank_placeholder')
                "
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="bank in banksList" :key="bank.id" :value="bank.id">
                {{ bank.title }}
              </SelectItem>
            </SelectContent>
          </Select>
          <div class="flex items-center space-x-2 pt-1">
            <Checkbox
              id="all-banks"
              :model-value="allBanks"
              @update:model-value="(val: boolean) => (allBanks = val)"
            />
            <Label for="all-banks" class="cursor-pointer text-sm">
              {{ t('rate_alert.all_banks') }}
            </Label>
          </div>
        </template>
        <p v-if="bankError" class="text-sm font-medium text-red-500">
          {{ bankError }}
        </p>
      </div>

      <p class="text-gray-600">
        {{ t('rate_alert.intro') }}
      </p>

      <!-- Error Alert -->
      <Alert v-if="serverError" variant="red">
        <LucideAlertCircle class="h-4 w-4" />
        <AlertDescription>
          {{ serverError }}
        </AlertDescription>
      </Alert>

      <form
        id="rate-alert-form"
        v-webmcp-tool="{
          name: 'create_rate_alert',
          description:
            'Subscribe to a notification when the buy or sell exchange rate of a currency in a bank reaches the given value.'
        }"
        class="flex flex-col gap-5"
        @submit="onSubmit"
      >
        <div class="grid grid-cols-2 items-start gap-4">
          <FormField v-slot="{ componentField }" name="type">
            <FormItem>
              <FormLabel>{{ t('rate_alert.operation_type') }}</FormLabel>
              <Select v-bind="componentField">
                <SelectTrigger :aria-label="t('rate_alert.select_operation')">
                  <SelectValue :placeholder="t('rate_alert.select_operation')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buy">{{ t('common.buy') }}</SelectItem>
                  <SelectItem value="sell">{{ t('common.sell') }}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          </FormField>

          <div class="space-y-1.5">
            <Label class="text-sm">{{ t('common.currency') }}</Label>
            <Select v-model="selectedCurrency">
              <SelectTrigger :aria-label="t('common.currency')">
                <SelectValue :placeholder="t('common.currency')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="opt in currencyOptions" :key="opt.value" :value="opt.value">
                  <div class="flex items-center gap-2">
                    <CurrencyIcon :code="opt.value" size="sm" country />
                    {{ opt.label }}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div class="space-y-1.5">
            <Label class="text-sm">{{ t('rate_alert.condition') }}</Label>
            <Select v-model="condition">
              <SelectTrigger :aria-label="t('rate_alert.select_condition')">
                <SelectValue :placeholder="t('rate_alert.select_condition')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lte">{{ t('rate_alert.condition_lte') }}</SelectItem>
                <SelectItem value="gte">{{ t('rate_alert.condition_gte') }}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <FormField v-slot="{ errors }" name="value">
            <FormItem>
              <FormLabel>{{ t('rate_alert.target_value') }}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  :model-value="selectedAmount ?? ''"
                  :class="{ 'border-red-500 bg-red-50': errors.length }"
                  :placeholder="t('common.amount')"
                  @update:model-value="
                    (val) => {
                      if (!val || val === '') {
                        selectedAmount = undefined
                        return
                      }
                      const num = parseFloat(String(val).replace(',', '.'))
                      selectedAmount = isNaN(num) ? undefined : num
                    }
                  "
                />
              </FormControl>
              <FormMessage />
              <p v-if="currentRateForValidation" class="text-xs text-gray-500">
                {{
                  t('rate_alert.current_rate', {
                    rate: useCurrencyFormat(currentRateForValidation, 1)
                  })
                }}
              </p>
            </FormItem>
          </FormField>

          <div class="space-y-1.5">
            <Label class="text-sm">{{ t('common.rate_type') }}</Label>
            <div class="flex h-9 items-center gap-2">
              <Switch id="rate-type" v-model="cashless" class="data-[state=checked]:bg-blue-600" />
              <Label for="rate-type" class="cursor-pointer text-sm">
                {{ t('common.cashless') }}
              </Label>
            </div>
          </div>

          <FormField v-slot="{}" name="expired_at">
            <FormItem>
              <FormLabel>{{ t('rate_alert.expiration_date') }}</FormLabel>
              <FormControl>
                <Popover v-model:open="isOpenDate">
                  <PopoverTrigger as-child>
                    <button
                      type="button"
                      :class="[
                        'flex h-9 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-start text-sm ring-offset-white',
                        'focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        selectedDate ? 'text-gray-900' : 'text-gray-500'
                      ]"
                    >
                      <span>{{
                        selectedDate ? formatDate(selectedDate) : t('rate_alert.select_date')
                      }}</span>
                      <LucideCalendar class="h-4 w-4 text-gray-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent class="w-auto overflow-hidden p-0" align="start">
                    <Calendar
                      :model-value="selectedDate"
                      :min-value="today(getLocalTimeZone())"
                      :max-value="maxDate"
                      :week-starts-on="0"
                      :disable-link="true"
                      :locale="'ru'"
                      layout="month-and-year"
                      @update:model-value="
                        (value) => {
                          if (value) {
                            selectedDate = value
                            isOpenDate = false
                          }
                        }
                      "
                    />
                  </PopoverContent>
                </Popover>
              </FormControl>
              <p class="text-xs text-gray-500">{{ t('rate_alert.date_hint_7_days') }}</p>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

      </form>
      </div>
    </template>
  </div>
</template>
